import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../App';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Star, Clock, Calendar as CalendarIcon, Trash2, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const CompareOffersPage = () => {
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get('request_id');
  const { API, sessionToken } = useContext(AuthContext);
  const navigate = useNavigate();
  const [starredBids, setStarredBids] = useState([]);
  const [talleres, setTalleres] = useState({});
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch starred bids
      let url = `${API}/client/starred-bids`;
      if (requestId) url += `?request_id=${requestId}`;
      
      const [bidsRes, requestRes] = await Promise.all([
        axios.get(url, { headers: { Authorization: `Bearer ${sessionToken}` } }),
        requestId ? axios.get(`${API}/service-requests/${requestId}`, { headers: { Authorization: `Bearer ${sessionToken}` } }) : Promise.resolve({ data: null })
      ]);

      setStarredBids(bidsRes.data);
      setRequest(requestRes.data);

      // Fetch taller info for each bid
      const tallerIds = [...new Set(bidsRes.data.map(b => b.taller_id))];
      const tallerData = {};
      await Promise.all(tallerIds.map(async (tid) => {
        try {
          const tRes = await axios.get(`${API}/public/talleres/${tid}`);
          tallerData[tid] = tRes.data;
        } catch (e) { console.error('Error fetching taller info:', e); }
      }));
      setTalleres(tallerData);
    } catch (error) {
      toast.error('Error al cargar ofertas marcadas');
    } finally {
      setLoading(false);
    }
  }, [API, sessionToken, requestId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUnstar = async (bidId) => {
    try {
      await axios.post(`${API}/bids/${bidId}/star`, {}, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setStarredBids(prev => prev.filter(b => b.id !== bidId));
      toast.success('Oferta desmarcada');
    } catch (error) {
      toast.error('Error al desmarcar oferta');
    }
  };

  const handleAcceptBid = (bid) => {
    // Navigate back to BidsView to use the accept flow
    navigate(`/client/bids/${bid.service_request_id}`);
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('es-CR', {
      weekday: 'short', day: 'numeric', month: 'short'
    });
  };

  const formatCurrency = (amount) => {
    return `₡${amount.toLocaleString()}`;
  };

  // Find min/max values for highlighting
  const prices = starredBids.map(b => b.price);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const ratings = starredBids.map(b => talleres[b.taller_id]?.rating || 0);
  const maxRating = Math.max(...ratings);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-xl text-purple-600">Cargando ofertas...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate(requestId ? `/client/bids/${requestId}` : '/client/dashboard')} 
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>
                Comparar Ofertas
              </h1>
              {request && (
                <p className="text-sm text-slate-600">{request.service_type} - {request.description?.slice(0, 50)}...</p>
              )}
            </div>
          </div>
          <Badge className="bg-purple-100 text-purple-800">
            {starredBids.length} de 4 máx.
          </Badge>
        </div>

        {starredBids.length === 0 ? (
          <Card className="p-12 text-center">
            <Star className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No tienes ofertas marcadas</h3>
            <p className="text-slate-500 mb-6">
              Marca ofertas con la estrella ⭐ para compararlas aquí
            </p>
            <Button onClick={() => navigate('/client/dashboard')} data-testid="go-to-dashboard-btn">
              Ver mis solicitudes
            </Button>
          </Card>
        ) : (
          <>
            {/* Comparison Grid */}
            <div className={`grid gap-4 ${
              starredBids.length === 1 ? 'grid-cols-1 max-w-md mx-auto' :
              starredBids.length === 2 ? 'grid-cols-1 md:grid-cols-2' :
              starredBids.length === 3 ? 'grid-cols-1 md:grid-cols-3' :
              'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
            }`}>
              {starredBids.map((bid) => {
                const taller = talleres[bid.taller_id];
                const isLowestPrice = bid.price === minPrice;
                const isHighestRating = (taller?.rating || 0) === maxRating && maxRating > 0;
                const hasDates = bid.proposed_dates && bid.proposed_dates.length > 0;

                return (
                  <Card 
                    key={bid.id} 
                    className={`p-5 relative flex flex-col ${isLowestPrice ? 'ring-2 ring-green-400' : ''}`}
                    data-testid={`compare-card-${bid.id}`}
                  >
                    {/* Badges */}
                    <div className="absolute -top-2 -right-2 flex flex-col gap-1">
                      {isLowestPrice && (
                        <Badge className="bg-green-500 text-white text-xs">💰 Mejor precio</Badge>
                      )}
                      {isHighestRating && (
                        <Badge className="bg-yellow-500 text-white text-xs">⭐ Mejor valorado</Badge>
                      )}
                    </div>

                    {/* Remove button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute top-2 left-2 p-1 h-auto text-slate-400 hover:text-red-500"
                      onClick={() => handleUnstar(bid.id)}
                      data-testid={`unstar-btn-${bid.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>

                    {/* Taller Info */}
                    <div className="text-center mt-4 mb-4">
                      <h3 className="font-semibold text-slate-800 text-lg">
                        {taller?.company_name || taller?.name || 'Taller'}
                      </h3>
                      <div className="flex items-center justify-center gap-1 text-sm text-slate-600 mt-1">
                        <Star className="w-4 h-4 text-yellow-500 fill-current" />
                        <span>{(taller?.rating || 0).toFixed(1)}</span>
                        <span className="text-slate-400">({taller?.total_reviews || 0})</span>
                      </div>
                    </div>

                    {/* Price - Highlighted */}
                    <div className={`text-center py-4 rounded-lg mb-4 ${isLowestPrice ? 'bg-green-50' : 'bg-slate-50'}`}>
                      <p className="text-sm text-slate-500 mb-1">Precio</p>
                      <p className={`text-3xl font-bold ${isLowestPrice ? 'text-green-600' : 'text-purple-600'}`}>
                        {formatCurrency(bid.price)}
                      </p>
                    </div>

                    {/* Details */}
                    <div className="space-y-3 flex-grow">
                      {/* Estimated Time */}
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500 flex items-center gap-1">
                          <Clock className="w-4 h-4" /> Tiempo est.
                        </span>
                        <span className="font-medium">{bid.estimated_time}</span>
                      </div>

                      {/* Proposed Dates */}
                      {hasDates && (
                        <div className="text-sm">
                          <p className="text-slate-500 flex items-center gap-1 mb-2">
                            <CalendarIcon className="w-4 h-4" /> Fechas disponibles
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {bid.proposed_dates.slice(0, 3).map((d) => (
                              <Badge key={d} variant="outline" className="text-xs">
                                {formatDate(d)}
                              </Badge>
                            ))}
                            {bid.proposed_dates.length > 3 && (
                              <Badge variant="outline" className="text-xs">+{bid.proposed_dates.length - 3}</Badge>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Message excerpt */}
                      <div className="text-sm">
                        <p className="text-slate-500 mb-1">Mensaje</p>
                        <p className="text-slate-700 text-xs line-clamp-2">{bid.message}</p>
                      </div>
                    </div>

                    {/* Action */}
                    <Button
                      className="w-full mt-4 bg-green-600 hover:bg-green-700"
                      onClick={() => handleAcceptBid(bid)}
                      data-testid={`accept-compare-btn-${bid.id}`}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Seleccionar
                    </Button>
                  </Card>
                );
              })}
            </div>

            {/* Legend */}
            <div className="mt-8 flex justify-center gap-6 text-sm text-slate-600">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span>Mejor precio</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <span>Mejor valorado</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CompareOffersPage;
